import dayjs from 'dayjs';

// ExcelJS is large, so it is only loaded when someone imports or exports.
const loadExcel = async () => (await import('exceljs')).default;

// Same limits as the server: a week holds 0 to 15 FTE with up to two decimals.
export const MAX_FTE = 15;
export const MAX_WEEKS = 156;

const WEEK_LABEL = /^w\s*(\d+)$/i;
const PHASE_LABEL = /sprint|phase/i;
const PERSON_LABEL = /person|employee|assigned|resource name/i;
const DATE_LABEL = /week start|date/i;
const TOTAL_LABEL = /^total/i;

const BLUE = 'FF2952CC';
const LIGHT_BLUE = 'FFEEF2FF';
const GREY = 'FFF2F4F7';

// ------------------------------------------------------------------ export
// Layout: row 1 week labels, row 2 phases (merged over their weeks), row 3 week start dates,
// then one row per role, then a totals row. Import reads this same layout back.
export const exportPlanToExcel = async ({ grid, roles, employees, projectName }) => {
  const ExcelJS = await loadExcel();
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Resource plan', {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 3 }],
  });

  const weeks = grid.totalWeeks;
  const firstWeekCol = 3;
  const totalCol = firstWeekCol + weeks;
  const start = dayjs(grid.startDate);
  const roleName = (roleId) => roles.find((role) => role.roleId === roleId)?.name || '';
  const personLabel = (empCode) => {
    if (!empCode) return '';
    const emp = employees.find((e) => e.empCode === empCode);
    return emp?.name ? `${emp.name} (${empCode})` : empCode;
  };

  const weekRow = sheet.getRow(1);
  weekRow.getCell(1).value = 'Role';
  weekRow.getCell(2).value = 'Assigned to';
  for (let w = 1; w <= weeks; w++) weekRow.getCell(firstWeekCol + w - 1).value = `W${w}`;
  weekRow.getCell(totalCol).value = 'Person-weeks';

  const phaseRow = sheet.getRow(2);
  phaseRow.getCell(1).value = 'Sprint / phase';
  grid.phases.forEach((phase) => {
    const from = firstWeekCol + phase.startWeek - 1;
    const to = firstWeekCol + phase.endWeek - 1;
    phaseRow.getCell(from).value = phase.name;
    if (to > from) sheet.mergeCells(2, from, 2, to);
  });

  const dateRow = sheet.getRow(3);
  dateRow.getCell(1).value = 'Week starting';
  for (let w = 1; w <= weeks; w++) {
    const cell = dateRow.getCell(firstWeekCol + w - 1);
    cell.value = start.add((w - 1) * 7, 'day').toDate();
    cell.numFmt = 'dd-mmm';
  }

  const colLetter = (col) => sheet.getColumn(col).letter;
  const firstWeekLetter = colLetter(firstWeekCol);
  const lastWeekLetter = colLetter(totalCol - 1);

  grid.resources.forEach((resource, index) => {
    const rowNo = 4 + index;
    const row = sheet.getRow(rowNo);
    row.getCell(1).value = roleName(resource.roleId);
    row.getCell(2).value = personLabel(resource.empCode);
    resource.allocations.forEach((fte, i) => {
      row.getCell(firstWeekCol + i).value = fte;
    });
    row.getCell(totalCol).value = {
      formula: `SUM(${firstWeekLetter}${rowNo}:${lastWeekLetter}${rowNo})`,
    };
  });

  const firstDataRow = 4;
  const lastDataRow = 3 + grid.resources.length;
  const totalRow = sheet.getRow(lastDataRow + 1);
  totalRow.getCell(1).value = 'Total FTE / week';
  if (grid.resources.length > 0) {
    for (let col = firstWeekCol; col <= totalCol; col++) {
      const letter = colLetter(col);
      totalRow.getCell(col).value = { formula: `SUM(${letter}${firstDataRow}:${letter}${lastDataRow})` };
    }
  }

  // styling
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 30;
  for (let col = firstWeekCol; col < totalCol; col++) sheet.getColumn(col).width = 8;
  sheet.getColumn(totalCol).width = 14;

  const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const border = { style: 'thin', color: { argb: 'FFD0D5DD' } };
  const lastRowNo = totalRow.number;
  for (let r = 1; r <= lastRowNo; r++) {
    for (let c = 1; c <= totalCol; c++) {
      const cell = sheet.getRow(r).getCell(c);
      cell.border = { top: border, left: border, bottom: border, right: border };
      if (c >= firstWeekCol) cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
  }
  [weekRow, phaseRow].forEach((row) =>
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = fill(BLUE);
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    })
  );
  dateRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = fill(LIGHT_BLUE);
    cell.font = { color: { argb: BLUE } };
  });
  for (let r = firstDataRow; r <= lastDataRow; r++) sheet.getRow(r).getCell(1).font = { bold: true };
  totalRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = fill(GREY);
    cell.font = { bold: true };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(projectName || 'project').replace(/[\\/:*?"<>|]+/g, '_')} - resource plan.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};

// ------------------------------------------------------------------ import
// Plain value of a cell: formula results, rich text and hyperlinks are unwrapped.
const cellValue = (cell) => {
  const value = cell.value;
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object') {
    if ('result' in value) return value.result ?? null;
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text).join('');
    if ('text' in value) return value.text;
    return null;
  }
  return value;
};

const cellText = (cell) => {
  const value = cellValue(cell);
  return value === null || value instanceof Date ? '' : String(value).trim();
};

// Clamps to 0..MAX_FTE and rounds to two decimals.
export const toFte = (value) =>
  Math.round(Math.min(MAX_FTE, Math.max(0, Number(value) || 0)) * 100) / 100;

const snapFte = (raw) => {
  if (raw === null || raw === '') return { fte: 0, adjusted: false };
  const number = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  if (Number.isNaN(number)) return { fte: 0, adjusted: true };
  const fte = toFte(number);
  return { fte, adjusted: fte !== number };
};

// Reads the first sheet that has a row of week labels (W1, W2, ...).
// Returns { startDate, totalWeeks, phases, rows: [{ roleName, person, allocations }], adjustedCells }.
export const parsePlanFile = async (file) => {
  const ExcelJS = await loadExcel();
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch (err) {
    throw new Error('Could not read the file. Please upload an .xlsx workbook.');
  }

  for (const sheet of workbook.worksheets) {
    const parsed = parseSheet(sheet);
    if (parsed) return parsed;
  }
  throw new Error('No week header row (W1, W2, ...) was found in the workbook.');
};

const parseSheet = (sheet) => {
  // 1. header row with week labels
  let headerRowNo = null;
  const weekCols = new Map(); // column number -> week number
  sheet.eachRow({ includeEmpty: false }, (row, rowNo) => {
    if (headerRowNo) return;
    const found = new Map();
    row.eachCell((cell, col) => {
      const match = cellText(cell).match(WEEK_LABEL);
      if (match) found.set(col, Number(match[1]));
    });
    if (found.size > 0) {
      headerRowNo = rowNo;
      found.forEach((week, col) => weekCols.set(col, week));
    }
  });
  if (!headerRowNo) return null;

  const totalWeeks = Math.max(...weekCols.values());
  if (totalWeeks > MAX_WEEKS) {
    throw new Error(`The sheet has ${totalWeeks} weeks; the limit is ${MAX_WEEKS}.`);
  }
  const firstWeekCol = Math.min(...weekCols.keys());
  const headerRow = sheet.getRow(headerRowNo);

  // 2. role column is the first labelled column left of the weeks; person column is optional
  let roleCol = 1;
  let personCol = null;
  for (let col = 1; col < firstWeekCol; col++) {
    const label = cellText(headerRow.getCell(col));
    if (PERSON_LABEL.test(label)) personCol = col;
  }
  if (personCol === roleCol) roleCol = personCol === 1 ? 2 : 1;

  // 3. phase and date rows sit next to the header: a phase row is labelled "Sprint"/"Phase" and has
  //    names under the weeks; a date row has dates under the weeks. The row nearest the header wins,
  //    so a title like "Resource count per sprint" above the table is not mistaken for phases.
  let phaseRowNo = null;
  let dateRowNo = null;
  const candidates = [1, -1, 2, -2, 3]
    .map((offset) => headerRowNo + offset)
    .filter((rowNo) => rowNo >= 1);
  candidates.forEach((rowNo) => {
    const row = sheet.getRow(rowNo);
    const label = cellText(row.getCell(roleCol));
    const weekValues = [...weekCols.keys()].map((col) => cellValue(row.getCell(col)));
    const hasDates = weekValues.some((value) => value instanceof Date);
    const hasNames = weekValues.some((value) => typeof value === 'string' && value.trim() && Number.isNaN(Number(value)));
    if (!dateRowNo && hasDates && (DATE_LABEL.test(label) || !label)) dateRowNo = rowNo;
    else if (!phaseRowNo && hasNames && PHASE_LABEL.test(label)) phaseRowNo = rowNo;
  });

  const phases = [];
  if (phaseRowNo) {
    const phaseRow = sheet.getRow(phaseRowNo);
    let current = null;
    [...weekCols.entries()]
      .sort((a, b) => a[1] - b[1])
      .forEach(([col, week]) => {
        const cell = phaseRow.getCell(col);
        const name = cellText(cell);
        // merged cells share a master; separate merges with the same name stay separate phases
        const groupKey = cell.isMerged ? cell.master.address : cell.address;
        if (current && name && current.name === name && current.key === groupKey && current.endWeek === week - 1) {
          current.endWeek = week;
        } else if (name) {
          current = { name: name.slice(0, 60), startWeek: week, endWeek: week, key: groupKey };
          phases.push(current);
        } else {
          current = null;
        }
      });
  }

  let startDate = null;
  if (dateRowNo) {
    const firstDate = cellValue(sheet.getRow(dateRowNo).getCell(firstWeekCol));
    if (firstDate instanceof Date) startDate = dayjs(firstDate).format('YYYY-MM-DD');
  }

  // 4. role rows: everything below the header block with a role name that isn't a total
  const skipRows = new Set([headerRowNo, phaseRowNo, dateRowNo]);
  const firstDataRow = Math.max(headerRowNo, phaseRowNo || 0, dateRowNo || 0) + 1;
  const rows = [];
  let adjustedCells = 0;
  for (let rowNo = firstDataRow; rowNo <= sheet.rowCount; rowNo++) {
    if (skipRows.has(rowNo)) continue;
    const row = sheet.getRow(rowNo);
    const roleName = cellText(row.getCell(roleCol));
    if (!roleName || TOTAL_LABEL.test(roleName)) continue;

    const allocations = Array(totalWeeks).fill(0);
    for (const [col, week] of weekCols) {
      const { fte, adjusted } = snapFte(cellValue(row.getCell(col)));
      allocations[week - 1] = fte;
      if (adjusted) adjustedCells += 1;
    }
    rows.push({
      roleName: roleName.slice(0, 120),
      person: personCol ? cellText(row.getCell(personCol)) : '',
      allocations,
    });
  }

  return {
    startDate,
    totalWeeks,
    phases: phases.map(({ key, ...phase }) => phase),
    rows,
    adjustedCells,
  };
};

// Finds the employee a "Name (CODE)", "CODE" or exact-name cell refers to.
export const matchEmployee = (person, employees) => {
  if (!person) return null;
  const code = person.match(/\(([^)]+)\)\s*$/)?.[1]?.trim() || person.trim();
  const byCode = employees.find((emp) => emp.empCode?.toLowerCase() === code.toLowerCase());
  if (byCode) return byCode.empCode;
  const byName = employees.filter((emp) => emp.name?.toLowerCase() === person.trim().toLowerCase());
  return byName.length === 1 ? byName[0].empCode : null;
};
